/**
 * DownloadToast - Toast component showing download progress
 *
 * Features:
 * - Progress bar for single and bulk downloads
 * - File count indicator
 * - Status icons (loading, complete, error)
 * - Dismiss button
 */

import {
  CheckCircle,
  Download,
  FileArchive,
  Loader2,
  X,
  XCircle,
} from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DownloadToastFile {
  fileName: string;
  status: 'pending' | 'fetching' | 'zipping' | 'error' | 'complete' | 'fallback';
  progress: number;
}

interface DownloadToastProps {
  /** Files being downloaded */
  files: DownloadToastFile[];
  /** Whether the zip file is being generated */
  isGeneratingZip?: boolean;
  /** Error message if download failed */
  error?: string | null;
  /** Callback when user dismisses the toast */
  onDismiss?: () => void;
}

const STATUS_LABELS: Record<DownloadToastFile['status'], string> = {
  pending: 'Waiting...',
  fetching: 'Downloading...',
  zipping: 'Preparing...',
  complete: 'Complete',
  error: 'Failed',
  fallback: 'Downloaded separately',
};

export const DownloadToast: React.FC<DownloadToastProps> = ({
  files,
  isGeneratingZip = false,
  error,
  onDismiss,
}) => {
  const totalFiles = files.length;
  const completedFiles = files.filter((f) => f.status === 'complete').length;
  const fallbackFiles = files.filter((f) => f.status === 'fallback').length;
  const errorFiles = files.filter((f) => f.status === 'error').length;
  const isBulkDownload = totalFiles > 1;

  // Calculate overall progress
  const overallProgress =
    totalFiles > 0
      ? Math.round(files.reduce((sum, f) => sum + f.progress, 0) / totalFiles)
      : 0;

  // Files are done if they're either complete (in zip) or fallback (downloaded separately)
  const doneFiles = completedFiles + fallbackFiles;
  const isComplete = doneFiles === totalFiles && !isGeneratingZip;
  const hasErrors = errorFiles > 0;
  const hasFallbacks = fallbackFiles > 0;
  const isInProgress = !isComplete && !hasErrors;
  const isPartialSuccess = isComplete && hasFallbacks && completedFiles > 0;

  // Get current status label
  const getStatusLabel = () => {
    if (error) return 'Download failed';
    if (isGeneratingZip) return 'Creating zip file...';

    if (isComplete) {
      if (isPartialSuccess) {
        // Some files in zip, some downloaded separately
        return `${completedFiles} in zip, ${fallbackFiles} downloaded separately`;
      }
      if (fallbackFiles === totalFiles) {
        // All files downloaded separately (no zip)
        return isBulkDownload
          ? 'All files downloaded separately'
          : 'Downloaded';
      }
      return isBulkDownload ? 'All files downloaded' : 'Downloaded';
    }

    if (hasErrors) return `${errorFiles} of ${totalFiles} failed`;

    const activeFile = files.find(
      (f) => f.status === 'fetching' || f.status === 'zipping',
    );
    if (activeFile) {
      return isBulkDownload
        ? `Downloading ${doneFiles + 1} of ${totalFiles}...`
        : STATUS_LABELS[activeFile.status];
    }

    return 'Starting download...';
  };

  return (
    <div className="flex flex-col gap-2.5 p-3 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 w-[320px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isInProgress && (
            <div className="relative">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            </div>
          )}
          {isComplete && !hasFallbacks && (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
          {isComplete && hasFallbacks && (
            <CheckCircle className="h-4 w-4 text-amber-500" />
          )}
          {hasErrors && <XCircle className="h-4 w-4 text-red-600" />}
          <span className="font-medium text-sm text-zinc-900 dark:text-white">
            {isBulkDownload ? (
              <span className="flex items-center gap-1.5">
                <FileArchive className="h-3.5 w-3.5" />
                Downloading {totalFiles} files
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Downloading file
              </span>
            )}
          </span>
        </div>
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-6 w-6 p-0 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out rounded-full',
              isInProgress && 'bg-blue-600',
              isComplete && !hasFallbacks && 'bg-green-600',
              isComplete && hasFallbacks && 'bg-amber-500',
              hasErrors && 'bg-red-600',
            )}
            style={{ width: `${isComplete ? 100 : overallProgress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500 dark:text-zinc-400">
            {getStatusLabel()}
          </span>
          {isBulkDownload && !error && (
            <span className="text-zinc-600 dark:text-zinc-300 font-medium">
              {doneFiles}/{totalFiles}
            </span>
          )}
        </div>
      </div>

      {/* Single file name (for single downloads) */}
      {!isBulkDownload && files[0] && (
        <p
          className="text-xs text-zinc-500 dark:text-zinc-400 truncate"
          title={files[0].fileName}
        >
          {files[0].fileName}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          {error}
        </p>
      )}
    </div>
  );
};

export default DownloadToast;
